/**
 * Générateur — Automatisme : placer sur une droite graduée un point dont
 * l'abscisse est un nombre relatif.
 * Attendu ID : 1234 | Niveau : 3ème | Domaine : Espace et géométrie, Repérage
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1061-1066 ; mêmes mots que
 * l'automatisme 4e l. 982-987, sans gen en base : ce gen sert les deux).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1064 (5e, demi-droite, décimaux positifs) à la droite
 * graduée RELATIVE : entiers −9..6 (pas 1), demis (pas 0,5), dixièmes
 * (fenêtre de 3 unités contenant 0 ou autour d'un entier négatif) ; les
 * régimes de gen_706 (objectif 5e) resserrés. Centièmes et fractions exclus
 * (À ARBITRER) ; un négatif par item au moins, un positif une fois sur trois.
 * Banque MathsMentales 6NA4 relevée (entiers 1..15, dixièmes).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Place le point » (placer_points, trois clés entiers / demis /
 *     dixièmes) : deux points, dont un négatif, sur la droite native.
 *   Carte 2 « Où se place le point ? » (placer_qcm_unique) : QUATRE DROITES
 *     en images, le point A en −2,7 sur l'une, +2,7 / −1,3 / −2,3 sur les
 *     autres (les phrases de 1064 sont illisibles en relatifs).
 *   Carte 3 « Combien de graduations ? » (placer_saisie) : « Pour placer
 *     −2,7 : je pars de −2 et je compte ⬚ petites graduations vers la
 *     gauche » (7), ou de −3 vers la droite (3).
 *   Carte 4 « Vrai ou faux » (placer_tableau_vf) : quatre lignes sur une
 *     droite en dixièmes portant A, B, C.
 *
 * INVARIANTS : dixièmes entiers ; les points de la carte 1 sont sur la grille
 * de calage du composant (start multiple du pas, jamais aux bornes) ; les
 * figures portent data-droite / data-points ; leurres distincts et DANS la
 * fenêtre (un leurre hors de la droite dessinée est mort, 1065 A3).
 * SACRIFIÉ (fiche « De −2, je compte 7 sauts vers la gauche ») : −2,7, partagé
 * avec 1235, exclu de tout tirage (droiteRelative.js), auto-testé.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   sens_inverse_negatif  : −1,3 pour −2,7 (de −2, on compte vers 0 comme pour un positif), PRINCIPALE ;
 *   signe_inverse         : +2,7 ;
 *   rang_depuis_la_gauche : −2,3 (la 7e graduation après −3) ;
 *   decale_un_trait       : −2,6 / −2,8.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, basePayload,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import {
    frD, saisieD, dec, tirerRegime, reperes, tirerPoints, tirerLettres, svgDroite, INTRO,
    leurresAbscisse, troisLeurres, grille,
} from './droiteRelative';

const ATTENDU_ID = 1234;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PLACER = 'Place le point';
const L_OU = 'Où se place le point ?';
const L_COMBIEN = 'Combien de graduations ?';
const L_VF = 'Vrai ou faux';

const EXPLICATIONS = {
    regle: 'À gauche de 0, je pars de l\'entier le plus proche de 0 et je m\'en éloigne : −2,7, c\'est −2 puis 7 petites graduations vers la gauche.',
    sens_inverse_negatif: 'De −2, les 7 petites graduations se comptent vers la gauche, en s\'éloignant de 0 : vers la droite, on arrive à −1,3.',
    signe_inverse: '−2,7 est à gauche de 0 ; 2,7 est à droite.',
    rang_depuis_la_gauche: '−2,7 est entre −3 et −2, à 3 petites graduations de −3 (pas 7).',
    decale_un_trait: 'Je recompte les petites graduations une à une depuis −2.',
};

// ─── Carte 1 : place le point (interactif) ────────────────────────────────

const genPlacer = (nom) => () => {
    const reg = tirerRegime(nom);
    const positif = randInt(1, 3) === 1 && grille(reg, (v) => v > 0).length > 0;
    let pts = tirerPoints(reg, 2, positif ? 1 : 2);
    if (positif && !pts.some((v) => v > 0)) pts = [pts[0], randomChoice(grille(reg, (v) => v > 0))];
    const lettres = tirerLettres(2);
    const bonne = {}; lettres.forEach((l, i) => { bonne[l] = saisieD(pts[i]); });
    const liste = lettres.map((l, i) => `${l}(${frD(pts[i])})`).join(' et ');
    const payload = basePayload('association', `<p>${INTRO(reg)}</p><p>Place les points ${liste} sur la droite graduée.</p>`, {
        subType: 'placer_points',
        numberline: { start: dec(reg.start), end: dec(reg.end), step: dec(reg.pas), reperes: reperes(reg) },
        pointsToPlace: lettres, bonne_reponse: JSON.stringify(bonne),
    }, opts(L_PLACER));
    payload.contenu = { ...payload.contenu, sousType: reg.nom, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, points: pts };
    return payload;
};

// ─── Carte 2 : où se place le point ? (QCM d'images) ─────────────────────

/** Un choix de QCM en image : `imageData` (convention des QCM d'images du parc, gen_1060). */
const choixImage = (reg, d) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svgDroite(reg, [{ d, nom: 'A' }]))}` } });

const genOuQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const reg = tirerRegime();
        const negatif = randInt(1, 4) !== 1;
        const cands = grille(reg, (v) => (negatif ? v < 0 : v > 0));
        if (!cands.length) continue;
        const x = randomChoice(cands);
        const faux = troisLeurres(leurresAbscisse(x, reg));
        if (!faux) continue;
        const bon = choixImage(reg, x);
        const choix = [bon, ...faux.map((f) => choixImage(reg, f.v))];
        const texte = `<p>${INTRO(reg)}</p><p><b>Sur quelle droite le point A a-t-il pour abscisse ${frD(x)} ?</b></p>`;
        const payload = makeQcm(shuffle(choix), bon, texte, opts(L_OU));
        payload.contenu = { ...payload.contenu, sousType: reg.nom, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, x, distracteurs: faux.map(({ code, v }) => ({ code, v })) };
        return payload;
    }
    throw new Error('gen_1234 : QCM d\'images sans tirage');
};

// ─── Carte 3 : combien de graduations ? (saisie) ─────────────────────────

const genCombien = () => {
    const reg = tirerRegime('dixiemes');
    const x = randomChoice(grille(reg, (v) => v < 0));
    const depuisDroite = randInt(0, 1) === 1;                    // de −2 vers la gauche (7) ou de −3 vers la droite (3)
    const plafond = Math.ceil(x / 10) * 10;
    const depart = depuisDroite ? plafond : plafond - 10;
    const n = depuisDroite ? plafond - x : x - (plafond - 10);
    const sens = depuisDroite ? 'la gauche' : 'la droite';
    const texte = `<p>${INTRO(reg)}</p><p>Pour placer le point d'abscisse ${frD(x)} : je pars de ${frD(depart)} et je compte combien de petites graduations vers ${sens} ?</p>`;
    const payload = makeSaisie(String(n), texte, { ...opts(L_COMBIEN), suffixe: 'graduations', elements_additionnels: imgElement(svgDroite(reg), 'large') });
    payload.contenu = { ...payload.contenu, sousType: depuisDroite ? 'vers_la_gauche' : 'vers_la_droite', explications: EXPLICATIONS };
    payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, x, depart, n };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

const grads = (n) => (n === 1 ? 'petite graduation' : 'petites graduations');

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const reg = tirerRegime('dixiemes');
        const pts = tirerPoints(reg, 3, 2).sort((a, b) => a - b);
        if (pts.length < 3 || pts[1] - pts[0] < 4 || pts[2] - pts[1] < 4) continue;   // étiquettes jamais superposées
        const noms = ['A', 'B', 'C'];
        const P = randInt(0, 2); const Q = randomChoice([0, 1, 2].filter((i) => i !== P));
        const x = pts[P];
        if (x >= 0) continue;
        const plafond = Math.ceil(x / 10) * 10; const f = plafond - x;
        const voisin = randomChoice(grille(reg, (v) => v !== x && Math.abs(v - x) <= 4 && Math.abs(v - x) >= 2));
        if (voisin === undefined) continue;
        const candidats = [
            { l: `Pour placer ${frD(x)}, je pars de ${frD(plafond)} et je compte ${f} ${grads(f)} vers la gauche.`, v: true, fam: 'geste' },
            { l: `Pour placer ${frD(x)}, je pars de ${frD(plafond)} et je compte ${f} ${grads(f)} vers la droite.`, v: false, fam: 'geste' },
            ...(f !== 5 ? [{ l: `Pour placer ${frD(x)}, je pars de ${frD(plafond - 10)} et je compte ${f} ${grads(f)} vers la droite.`, v: false, fam: 'geste' }] : []),
            { l: `Le point d'abscisse ${frD(voisin)} se place à ${voisin < x ? 'gauche' : 'droite'} de ${noms[P]}.`, v: true, fam: 'voisin' },
            { l: `Le point d'abscisse ${frD(voisin)} se place à ${voisin < x ? 'droite' : 'gauche'} de ${noms[P]}.`, v: false, fam: 'voisin' },
            { l: `${noms[P]} est plus proche de 0 que ${noms[Q]}.`, v: Math.abs(x) < Math.abs(pts[Q]), fam: 'proche' },
            { l: `Le point d'abscisse ${frD(-x)} se place à gauche de 0.`, v: false, fam: 'signe' },
            { l: `Le point d'abscisse ${frD(x)} se place à gauche de 0.`, v: true, fam: 'signe' },
        ];
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((c) => !c.v)); const vraies = shuffle(candidats.filter((c) => c.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4 || new Set(lignes.map((c) => c.fam)).size !== 4) continue;
        const svg = svgDroite(reg, pts.map((d, i) => ({ d, nom: noms[i] })));
        const payload = makeVraiFaux(lignes.map((c) => c.l), lignes.map((c) => (c.v ? 0 : 1)), `<p>${INTRO(reg)}</p><p><b>Vrai ou faux ?</b></p>`, { ...opts(L_VF), elements_additionnels: imgElement(svg, 'large') });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, points: pts, x };
        return payload;
    }
    throw new Error('gen_1234 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'dixièmes entiers ; régimes entiers / demis / dixièmes ; un négatif par item, un positif une fois sur trois',
        'leurres dans la fenêtre dessinée et sur la grille ; −2,7 sacrifié (partagé avec 1235)',
    ],
    placer_qcm_unique: 'sens_inverse_negatif (−1,3 pour −2,7, la principale), signe_inverse (+2,7), rang_depuis_la_gauche (−2,3), decale_un_trait (±1 pas ; ±2 pas en complément sur les entiers)',
    placer_tableau_vf: 'quatre familles exclusives : le geste (départ et sens), le voisin (à gauche / à droite), le plus proche de 0, le signe',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    placer_entiers_placer_points: genPlacer('entiers'),
    placer_demis_placer_points: genPlacer('demis'),
    placer_dixiemes_placer_points: genPlacer('dixiemes'),
    ou_qcm_unique: genOuQcm,
    combien_saisie: genCombien,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'placer', label: L_PLACER, formatsList: [{ key: 'placer_entiers_placer_points', label: 'Entiers' }, { key: 'placer_demis_placer_points', label: 'Demis' }, { key: 'placer_dixiemes_placer_points', label: 'Dixièmes' }] },
    { id: 'ou', label: L_OU, formatsList: [{ key: 'ou_qcm_unique', label: 'QCM' }] },
    { id: 'combien', label: L_COMBIEN, formatsList: [{ key: 'combien_saisie', label: 'Saisie' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
